import { mergeClasses, Title2 } from "@fluentui/react-components";
import _ from "lodash";
import { memo, MouseEvent, MouseEventHandler, useMemo, useState } from "react";
import { SuggestionTypeSwitch } from "../../../components/SuggestionTypeSwitch";
import { DEFAULT_APPLICATION_ID, DEFAULT_WORKSPACE_ID } from "../../../constants";
import { useAppData, useContextApp, useLocalDBStore } from "../../../hooks";
import { ApplicationType, SuggestionAppPage, SuggestionSearchMode } from "../../../types/enums";
import KeywordTransformer from "../../../utils/keywordTransformer";
import { ActionButtons } from "../../SuggestionResult/components/ActionButtons";
import { useSearchSectionStyles } from "../styles/SearchSection.styles";
import { DropboxTextArea } from "./DropboxTextArea";
import { WorkspaceMenuSelector } from "./WorkspaceMenuSelector";

export const SearchSection = memo(() => {
  const styles = useSearchSectionStyles();
  const [fullAppView] = useState<boolean>(false);
  const { createContextApp } = useContextApp();
  const { SaveSearchHistory } = useLocalDBStore();
  const [searchType, setSearchType] = useState<SuggestionSearchMode>(SuggestionSearchMode.SEMANTIC_SEARCH);
  const [searchText, setSearchText] = useState<string>("");

  const defaultWorkspaceId: string = DEFAULT_WORKSPACE_ID;
  const handleSearchTypeChange = (type: SuggestionSearchMode) => {
    setSearchType(type);
  };
  const [workspaceId, setWorkspaceId] = useAppData<string>(
    DEFAULT_APPLICATION_ID,
    "workspaceId",
    `${defaultWorkspaceId}`
  );

  const onQuickSearchClicked: MouseEventHandler = useMemo(
    () =>
      _.debounce((_ev: MouseEvent) => {
        let keywordText = "";
        if (searchType === SuggestionSearchMode.KEYWORD_SEARCH) {
          const transformer = new KeywordTransformer();
          keywordText = transformer.sanitize(searchText.trim());
        }

        const appData = {
          searchType,
          searchText,
          keywordSearchText: keywordText,
          name: `Results - ${searchText}`,
          workspaceId,
          currentPage: SuggestionAppPage.SUGGESTION_RESULT,
          from: SuggestionAppPage.SEARCH_SUGGESTION,
        };
        createContextApp(ApplicationType.SUGGESTION_APP, appData);
        SaveSearchHistory(appData as any);
      }, 500),
    [searchType, searchText, workspaceId, createContextApp, SaveSearchHistory]
  );

  const onMoreSearchOptionClicked: MouseEventHandler = useMemo(
    () =>
      _.debounce((_ev: MouseEvent) => {
        createContextApp(ApplicationType.SUGGESTION_APP, {
          searchType,
          searchText,
          workspaceId,
        });
      }, 500),
    [searchType, searchText, workspaceId, createContextApp]
  );

  return (
    <div className={mergeClasses(styles.searchContainer, fullAppView ? styles.hideSearchContainer : "")}>
      <Title2 className={styles.title}>Search for Provisions</Title2>
      <DropboxTextArea autoFocus={true} resize="none" value={searchText} onChange={setSearchText} />
      <SuggestionTypeSwitch type={searchType} onChange={handleSearchTypeChange} />
      <WorkspaceMenuSelector value={workspaceId} onChange={setWorkspaceId} />
      <ActionButtons
        onUpdate={onQuickSearchClicked}
        onReset={onMoreSearchOptionClicked}
        updateLabel="Quick Search"
        resetLabel="More Search Options"
        className={styles.actions}
        secondBtnAppearance="secondary"
        disabled={!searchText}
        btnClassName={styles.action}
      />
    </div>
  );
});
